import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Globe, Copy, Check, Pencil, Loader2, ExternalLink, Upload, User as UserIcon, Lock, KeyRound } from "lucide-react";

/**
 * Compact card shown on the user's Vault view — toggle to make ALL their
 * public-marked generations visible at /u/<slug>. Auto-generates a slug on
 * first enable, editable afterwards.
 */
export default function PublicProfileCard() {
  const [state, setState] = useState({ is_public_profile: false, slug: null, name: "", avatar_url: null, has_password: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [slugDraft, setSlugDraft] = useState("");
  const [copied, setCopied] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const avatarInputRef = useRef(null);

  const publicUrl = state.slug ? `${window.location.origin}/u/${state.slug}` : "";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/profile");
        setState(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files allowed."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Avatar must be under 8MB."); return; }
    setAvatarUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/me/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      setState((s) => ({ ...s, avatar_url: data.avatar_url }));
      toast.success("Profile image updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setAvatarUploading(false);
    }
  };

  const changePassword = async () => {
    if (pwNew.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pwNew !== pwConfirm) { toast.error("Passwords don't match."); return; }
    setPwSaving(true);
    try {
      await api.patch("/me/password", { current_password: pwCurrent || undefined, new_password: pwNew });
      setPwOpen(false);
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
      setState((s) => ({ ...s, has_password: true }));
      toast.success(state.has_password ? "Password updated." : "Password set. You can now sign in with email + password.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setPwSaving(false);
    }
  };

  const toggle = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/me/profile", { is_public: next });
      setState((s) => ({ ...s, ...data }));
      if (next) toast.success("Your public profile is live");
      else toast.info("Public profile hidden");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update profile");
    } finally { setBusy(false); }
  };

  const saveSlug = async () => {
    const cleaned = slugDraft.trim().toLowerCase();
    if (!cleaned) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/me/profile", { is_public: true, slug: cleaned });
      setState((s) => ({ ...s, ...data }));
      setEditing(false);
      toast.success("Handle updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update handle");
    } finally { setBusy(false); }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Copy failed"); }
  };

  if (loading) return null;

  return (
    <div
      data-testid="share-to-public-card"
      className="relative mb-6 rounded-2xl bg-gradient-to-br from-[#120E15] via-[#120E15] to-[#0d1f24] border border-[#F2C94C]/25 overflow-hidden"
    >
      <div className="h-0.5 bg-gradient-to-r from-[#F2C94C] via-[#FF6B4A] to-[#00E5FF]" />
      <div className="p-5 md:p-6">
        {/* Avatar row */}
        <div className="mb-5 pb-5 border-b border-[#F2C94C]/10 flex items-center gap-4" data-testid="avatar-row">
          <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-[#F2C94C]/40 bg-[#0A0A0A] flex items-center justify-center">
            {state.avatar_url ? (
              <img src={state.avatar_url} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
            ) : (
              <UserIcon className="w-7 h-7 text-white" />
            )}
            {avatarUploading && (
              <div className="absolute inset-0 bg-[#0A0A0A]/70 flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-[#F2C94C]" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-widest">// PROFILE IMAGE</div>
            <div className="font-body text-sm text-white">{state.name || "Anonymous creator"}</div>
            <div className="font-mono-hy text-[9px] text-white">PNG · JPG · WEBP · up to 8MB</div>
          </div>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="avatar-upload-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAvatar(f); e.target.value = ""; }}
          />
          <button
            onClick={() => avatarInputRef.current?.click()}
            disabled={avatarUploading}
            data-testid="avatar-upload-btn"
            className="font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#F2C94C]/40 text-[#F2C94C] hover:bg-[#F2C94C]/10 disabled:opacity-50 flex items-center gap-2"
          >
            {avatarUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {state.avatar_url ? "Change" : "Upload"}
          </button>
        </div>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#F2C94C]/10 border border-[#F2C94C]/25 flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-[#F2C94C]" />
            </div>
            <div className="min-w-0">
              <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-1 tracking-widest">
                {state.is_public_profile ? "// PROFILE · LIVE" : "// PROFILE · PRIVATE"}
              </div>
              <div className="font-display text-lg text-white leading-tight mb-1">
                {state.is_public_profile ? "Your dynasty is on air" : "Share to Public"}
              </div>
              <p className="font-body text-sm text-white">
                {state.is_public_profile
                  ? "Anyone with your link can browse your public creations — no login needed."
                  : "Turn this on and your public creations become a browsable profile page."}
              </p>
            </div>
          </div>
          <Switch
            checked={!!state.is_public_profile}
            onCheckedChange={toggle}
            disabled={busy}
            data-testid="share-to-public-toggle"
            className="data-[state=checked]:bg-[#F2C94C]"
          />
        </div>

        {state.is_public_profile && state.slug && (
          <div className="mt-5 pt-5 border-t border-[#F2C94C]/10">
            <div className="font-mono-hy text-[10px] text-[#00E5FF] tracking-widest mb-2">// PUBLIC URL</div>
            {editing ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="slug-edit-row">
                <div className="flex-1 min-w-[240px] flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#0A0A0A] border border-[#00E5FF]/30 focus-within:border-[#00E5FF]">
                  <span className="font-mono-hy text-[10px] text-white">/u/</span>
                  <input
                    autoFocus
                    value={slugDraft}
                    onChange={(e) => setSlugDraft(e.target.value)}
                    placeholder={state.slug}
                    data-testid="slug-edit-input"
                    className="bg-transparent flex-1 outline-none font-mono text-sm text-white placeholder:text-white"
                  />
                </div>
                <button
                  onClick={saveSlug}
                  disabled={busy || !slugDraft.trim()}
                  data-testid="slug-save-btn"
                  className="font-mono-hy text-[10px] px-4 py-2.5 rounded-xl bg-[#00E5FF] text-[#0A0A0A] hover:bg-[#7BF0FF] disabled:opacity-60 flex items-center gap-2"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
                </button>
                <button
                  onClick={() => { setEditing(false); setSlugDraft(""); }}
                  className="font-mono-hy text-[10px] px-4 py-2.5 rounded-xl border border-[#F2C94C]/20 text-white hover:text-white"
                >Cancel</button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap" data-testid="public-url-row">
                <div className="flex-1 min-w-[240px] px-4 py-2.5 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/15 font-mono text-xs text-white truncate">
                  {publicUrl}
                </div>
                <button
                  onClick={copyUrl}
                  data-testid="copy-public-url-btn"
                  className="font-mono-hy text-[10px] px-4 py-2.5 rounded-xl bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] flex items-center gap-2"
                >
                  {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy Link</>}
                </button>
                <a
                  href={publicUrl}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="open-public-profile-link"
                  className="font-mono-hy text-[10px] px-4 py-2.5 rounded-xl border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] flex items-center gap-2"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
                <button
                  onClick={() => { setSlugDraft(state.slug || ""); setEditing(true); }}
                  data-testid="edit-slug-btn"
                  className="font-mono-hy text-[10px] px-4 py-2.5 rounded-xl border border-[#F2C94C]/20 text-white hover:text-[#F2C94C] hover:border-[#F2C94C] flex items-center gap-2"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit handle
                </button>
              </div>
            )}
            <p className="mt-3 font-mono-hy text-[9px] text-white leading-relaxed">
              Only your generations marked with the public eye icon show up on your profile. Use the eye toggle on any tile to include or hide it individually.
            </p>
          </div>
        )}

        {/* Password change section */}
        <div className="mt-5 pt-5 border-t border-[#F2C94C]/10" data-testid="password-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-[#00E5FF]" />
              <div>
                <div className="font-mono-hy text-[10px] text-[#00E5FF] tracking-widest">// PASSWORD</div>
                <div className="font-body text-sm text-white">
                  {state.has_password ? "Change your sign-in password" : "Add a password to enable email sign-in"}
                </div>
              </div>
            </div>
            <button
              onClick={() => setPwOpen((v) => !v)}
              data-testid="password-toggle-btn"
              className="font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 flex items-center gap-2"
            >
              <KeyRound className="w-3.5 h-3.5" /> {pwOpen ? "Close" : (state.has_password ? "Change" : "Set")}
            </button>
          </div>
          {pwOpen && (
            <div className="mt-4 space-y-2" data-testid="password-form">
              {state.has_password && (
                <input
                  type="password"
                  placeholder="Current password"
                  value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)}
                  data-testid="password-current-input"
                  className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/20 focus:border-[#00E5FF] focus:outline-none font-body text-sm text-white placeholder:text-white"
                />
              )}
              <input
                type="password"
                placeholder="New password (min 8 chars)"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                data-testid="password-new-input"
                className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/20 focus:border-[#00E5FF] focus:outline-none font-body text-sm text-white placeholder:text-white"
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                data-testid="password-confirm-input"
                className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/20 focus:border-[#00E5FF] focus:outline-none font-body text-sm text-white placeholder:text-white"
              />
              <button
                onClick={changePassword}
                disabled={pwSaving || pwNew.length < 8 || pwNew !== pwConfirm}
                data-testid="password-save-btn"
                className="w-full font-mono-hy text-[10px] px-4 py-2.5 rounded-full bg-[#00E5FF] text-[#0A0A0A] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {pwSaving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : <><Check className="w-3.5 h-3.5" /> {state.has_password ? "Update password" : "Set password"}</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
